// Home dashboard — App Flow §2, empty state from Content §4.2.
import {
  Bell,
  Button,
  Card,
  EditionCoverCard,
  EmptyState,
  LoadingState,
  PageHeader,
  Section,
  StatusPill,
} from '@roots/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { InstallCard } from '../components/install-card.js';
import { PushPromptCard } from '../components/push-card.js';
import { api } from '../lib/api.js';
import { cycleStatusLabel, cycleTone } from '../lib/format.js';

export function HomeScreen() {
  const navigate = useNavigate();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['home'],
    queryFn: () => api.home(),
  });
  const unread = data?.unread_notifications ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Home"
        action={
          <Button
            variant="ghost"
            aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
            onClick={() => void navigate({ to: '/notifications' })}
          >
            <Bell size={24} aria-hidden />
            {unread > 0 && <span className="type-label">{unread}</span>}
          </Button>
        }
      />

      <InstallCard dismissible />
      <PushPromptCard />

      {isLoading && <LoadingState onRetry={() => void refetch()} />}

      {!isLoading && (data?.families.length ?? 0) === 0 && (
        <EmptyState
          message="Welcome to Roots! Start by creating a family or joining one."
          actions={
            <>
              <Button onClick={() => void navigate({ to: '/families' })}>Create a family</Button>
              <Button
                variant="secondary"
                onClick={() => void navigate({ to: '/families', search: { join: 1 } as never })}
              >
                I have an invite link
              </Button>
            </>
          }
        />
      )}

      {(data?.families.length ?? 0) > 0 && (
        <Section title="Your families">
          <div className="flex flex-col gap-3">
            {data?.families.map((family) => (
              <Card key={family.id} className="flex flex-col gap-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="type-heading">{family.name}</h3>
                    <p className="type-caption">{family.member_count} members</p>
                  </div>
                  <StatusPill tone={cycleTone(family.cycle?.status ?? null, family.cycle?.cutoff)}>
                    {cycleStatusLabel(family.cycle?.status ?? null, family.cycle?.cutoff)}
                  </StatusPill>
                </div>
                <p className="type-body">
                  {family.i_am_editor
                    ? 'You’re this month’s editor!'
                    : family.editor_name
                      ? `${family.editor_name} is this month’s editor`
                      : 'An editor will be picked soon'}
                </p>
                <Button variant="secondary" onClick={() => void navigate({ to: '/my-month' })}>
                  {family.unanswered_prompt_count > 0
                    ? `${family.unanswered_prompt_count} ${family.unanswered_prompt_count === 1 ? 'prompt' : 'prompts'} waiting`
                    : 'Open My Month'}
                </Button>
              </Card>
            ))}
          </div>
        </Section>
      )}

      {data?.latest_edition && (
        <Section title="Latest edition">
          <EditionCoverCard
            theme={data.latest_edition.theme}
            monthLabel={data.latest_edition.cover.month_label}
            familyName={data.latest_edition.family_name}
            isNew={data.latest_edition.is_new}
            onOpen={() =>
              void navigate({
                to: '/newsletter/$editionId',
                params: { editionId: data.latest_edition?.id as string },
              })
            }
          />
        </Section>
      )}
    </div>
  );
}
